import {
  buildExport,
  exportFilename,
  exportSheets,
  parseExport,
  partitionEntries,
  planRestore,
  reportFilename,
} from '@iculogbook/shared';
import type {
  AnaestheticCase,
  DateRange,
  EdtEntry,
  Entry,
  LocalAnaestheticCase,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
  RestorePlan,
} from '@iculogbook/shared';

import { db } from '../../db/db.ts';
import { anaestheticRepository } from '../../db/repositories/anaesthetic.ts';
import { casesRepository } from '../../db/repositories/cases.ts';
import { communicationsRepository } from '../../db/repositories/communications.ts';
import { edtRepository } from '../../db/repositories/edt.ts';
import { metaRepository } from '../../db/repositories/meta.ts';
import { proceduresRepository } from '../../db/repositories/procedures.ts';
import { xlsx } from './xlsxWrite.ts';

/**
 * Export and restore — the control for hazard H1, a trainee losing their
 * logbook.
 *
 * Everything here reads and writes through the repositories, with one
 * deliberate exception: the export reads soft-deleted entries too, because an
 * export is a copy of the record rather than a report of it.
 */

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();

  // Revoked on the next tick: revoking immediately can cancel the download in
  // some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Everything this device holds for a user, deleted entries included. */
async function everything(userId: string): Promise<{
  cases: LocalCase[];
  procedures: LocalProcedure[];
  edtEntries: LocalEdtEntry[];
  anaestheticCases: LocalAnaestheticCase[];
  communications: LocalCommunication[];
}> {
  const [
    live,
    deleted,
    liveProcedures,
    deletedProcedures,
    liveEdt,
    deletedEdt,
    liveAnaesthetic,
    deletedAnaesthetic,
    liveCommunications,
    deletedCommunications,
  ] = await Promise.all([
    casesRepository.list(userId),
    casesRepository.listDeleted(userId),
    proceduresRepository.list(userId),
    proceduresRepository.listDeleted(userId),
    edtRepository.list(userId),
    edtRepository.listDeleted(userId),
    anaestheticRepository.list(userId),
    anaestheticRepository.listDeleted(userId),
    communicationsRepository.list(userId),
    communicationsRepository.listDeleted(userId),
  ]);

  return {
    cases: [...live, ...deleted],
    procedures: [...liveProcedures, ...deletedProcedures],
    edtEntries: [...liveEdt, ...deletedEdt],
    anaestheticCases: [...liveAnaesthetic, ...deletedAnaesthetic],
    communications: [...liveCommunications, ...deletedCommunications],
  };
}

export async function exportJson(userId: string, displayName: string): Promise<string> {
  const exportedAt = new Date();
  const { cases, procedures, edtEntries, anaestheticCases, communications } =
    await everything(userId);

  const document = buildExport({
    displayName,
    cases,
    procedures,
    edtEntries,
    anaestheticCases,
    communications,
    exportedAt,
  });
  const filename = exportFilename(exportedAt, 'json');

  download(new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' }), filename);

  await metaRepository.setLastExportAt(exportedAt.toISOString());
  return filename;
}

/**
 * The logbook for one period as an Excel workbook — the companion to the PDF
 * on the same screen, and offered next to it rather than in Settings.
 *
 * Where the JSON and CSV exports in Settings are copies of the whole record,
 * this is a **report**: it covers the period the trainee chose, and it holds
 * live entries only, because `selectEntries` has already dropped the deleted
 * ones by the time they reach here. An ARCP summary must not carry an entry
 * its author deleted.
 *
 * It takes the entries rather than a user id so it works from exactly the
 * arrays the report screen has already computed and is showing counts for —
 * a second read of the database could disagree with what is on screen.
 *
 * Unlike the JSON export, and like the CSV, it does not reset the 90-day
 * backup reminder: it does not round-trip, so calling it a backup would tell
 * a trainee they were safe when they were not.
 */
export function exportXlsx(input: {
  displayName: string;
  range: DateRange;
  entries: readonly Entry[];
  edtEntries: readonly EdtEntry[];
  anaestheticCases: readonly AnaestheticCase[];
}): string {
  const { cases, procedures, communications } = partitionEntries(input.entries);
  const filename = reportFilename(input.displayName, input.range, 'xlsx');

  download(
    xlsx(
      exportSheets({
        cases,
        procedures,
        communications,
        edtEntries: input.edtEntries,
        anaestheticCases: input.anaestheticCases,
      }),
    ),
    filename,
  );

  return filename;
}

export type RestorePreview = { plan: RestorePlan; displayName: string; exportedAt: string };

/** Reads a file and works out what a restore would do. Writes nothing. */
export async function previewRestore(
  userId: string,
  file: File,
): Promise<{ ok: true; preview: RestorePreview } | { ok: false; reason: string }> {
  let contents: unknown;

  try {
    contents = JSON.parse(await file.text());
  } catch {
    return { ok: false, reason: 'That file is not valid JSON.' };
  }

  const parsed = parseExport(contents);
  if (!parsed.ok) return { ok: false, reason: parsed.reason };

  const [cases, procedures, edtEntries, anaestheticCases, communications] = await Promise.all([
    db.cases.toArray(),
    db.procedures.toArray(),
    db.edtEntries.toArray(),
    db.anaestheticCases.toArray(),
    db.communications.toArray(),
  ]);

  const existing = new Set(
    [...cases, ...procedures, ...edtEntries, ...anaestheticCases, ...communications]
      .filter((row) => row.userId === userId)
      .map((row) => row.id),
  );

  return {
    ok: true,
    preview: {
      plan: planRestore(parsed.value, existing),
      displayName: parsed.value.displayName,
      exportedAt: parsed.value.exportedAt,
    },
  };
}

/**
 * Writes a previewed restore.
 *
 * Restored entries are marked `source: 'restore'` and queued for sync like any
 * other local write, so they reach the server and the trainee's other devices.
 */
export async function applyRestore(userId: string, plan: RestorePlan): Promise<number> {
  const { cases, procedures, edtEntries, anaestheticCases, communications } = plan.toImport;
  if (
    cases.length === 0 &&
    procedures.length === 0 &&
    edtEntries.length === 0 &&
    anaestheticCases.length === 0 &&
    communications.length === 0
  ) {
    return 0;
  }

  /*
   * The parsed records are stored records without the local-only `syncedAt`;
   * the repository sets it to null as it writes, which is what queues them.
   */
  await casesRepository.restoreFromExport(userId, cases as LocalCase[]);
  await proceduresRepository.restoreFromExport(userId, procedures as LocalProcedure[]);
  await edtRepository.restoreFromExport(userId, edtEntries as LocalEdtEntry[]);
  await anaestheticRepository.restoreFromExport(
    userId,
    anaestheticCases as LocalAnaestheticCase[],
  );
  await communicationsRepository.restoreFromExport(
    userId,
    communications as LocalCommunication[],
  );

  return (
    cases.length +
    procedures.length +
    edtEntries.length +
    anaestheticCases.length +
    communications.length
  );
}
